import GameModel from './GameModel';
import GridBox from './model/grid/GridBox';
import Vector from './model/math/Vector';
import { Settings } from './Settings';

export default class Projection extends GameModel {
    constructor() {
        super();

        /**
         * The grid coordinate that is at the center of the screen.
         * @constant
         * @type {Vector}
         */
        this.gridCenter = new Vector(Settings.start.startX, Settings.start.startY);

        /**
         * @type {boolean}
         */
        this.stayCenteredOnCharacter = true;

        /**
         * @constant
         * @type {Vector}
         */
        this.screenOrigin = new Vector(0, 0);

        /**
         * @type {number}
         */
        this.visibleHalfWidth = 0;
        /**
         * @type {number}
         */
        this.visibleHalfHeight = 0;

        /**
         * @type {number}
         */
        this.visibleHalfWidthWorld = 0;
        /**
         * @type {number}
         */
        this.visibleHalfHeightWorld = 0;

        /**
         * @type {number}
         */
        this.screenScaleFactor = 1;

        /**
         * @type {number}
         */
        this.zoom = Settings.projection.zoomDefault;

        /**
         * This is set by the RunState after the game view has been created.
         * A new canvas element is created for every run.
         * @type {HTMLCanvasElement}
         */
        this.canvas = null;
    }

    /**
     * @param {HTMLCanvasElement} canvas 
     */
    setCanvas(canvas) {
        this.canvas = canvas;
    }

    resetProjectionState() {
        this.setGridCenter(Settings.projection.startCenterX, Settings.projection.startCenterY);
        this.zoom = Settings.projection.zoomDefault;

        this.canvas = null;
    }

    /**
     * Sets the coordinate of grid that is at the center of the screen.
     * @param {number} worldX
     * @param {number} worldY
     */
    setGridCenter(worldX, worldY) {
        this.gridCenter.set(worldX, worldY);
    }

    /**
     * @param {number} deltaX
     * @param {number} deltaY
     */
    updateSpaceCenter(deltaX, deltaY) {

        this.gridCenter.addXY(deltaX, deltaY);

        // if (this.gridCenter.y < 0) {
        //     this.gridCenter.y = 0;
        // }
        // if (this.gridCenter.y > Settings.grid.worldPixelHeight - 1) {  // Don't let y be equal to the max height.
        //     this.gridCenter.y = Settings.grid.worldPixelHeight - 1;
        // }
    }


    updateProjectionForFrame() {
        if (!this.canvas) {
            return;
        }

        // The projection always needs to know the current dimensions of the screen.
        this.visibleHalfWidth = this.canvas.width / 2;
        this.visibleHalfHeight = this.canvas.height / 2;

        this.visibleHalfWidthWorld = this.visibleHalfWidth / this.zoom;
        this.visibleHalfHeightWorld = this.visibleHalfHeight / this.zoom;


        // The screen origin is the grid center minus the center coordinate of the screen.
        this.screenOrigin.set(
            (this.zoom * this.gridCenter.x) - this.visibleHalfWidth,
            (this.zoom * this.gridCenter.y) - this.visibleHalfHeight
        );

        // We need to cast the screen origin coordinates to ints or else we'll get gaps between 
        // our sprites when we render stuff.
        this.screenOrigin.x = this.screenOrigin.x | 0;
        this.screenOrigin.y = this.screenOrigin.y | 0;
    }

    /**
     * @param {number} screenDistance
     * @returns {number}
     */
    screenToWorldDistance(screenDistance) {
        return screenDistance / this.zoom;
    }

    /**
     * @param {Vector} screenCoordinate
     * @param {Vector} worldCoordinate The output vector.
     */
    screenToWorld(screenCoordinate, worldCoordinate) {
        var factor = 1.0 / this.zoom;

        worldCoordinate.set(
            factor * (this.screenOrigin.x + screenCoordinate.x),
            factor * (this.screenOrigin.y + screenCoordinate.y)
        );
    }

    // /**
    //  * @param {Vector} screenCoordinate
    //  * @param {Vector} worldCoordinate The output vector.
    //  * @returns {number}
    //  */
    // screenToWorld(screenCoordinate, worldCoordinate) {
    //     var factor = 1.0 / this.zoom;
    //
    //     worldCoordinate.set(
    //         factor * (this.screenOrigin.x + screenCoordinate.x),
    //         factor * (this.screenOrigin.y + (Game.canvas.height - screenCoordinate.y))
    //     );
    // }

    /**
     * @param {Vector} worldCoordinate
     * @param {Vector} screenCoordinate The output vector.
     */
    worldToScreen(worldCoordinate, screenCoordinate) {
        screenCoordinate.x = (this.zoom * worldCoordinate.x) - this.screenOrigin.x;
        screenCoordinate.y = (this.zoom * worldCoordinate.y) - this.screenOrigin.y;
    }

    // /**
    //  * @param {Vector} worldCoordinate
    //  */
    // isWorldCoordinateVisible(worldCoordinate) {
    //     this.worldToScreen(worldCoordinate, this.internalScreenPosition);
    //
    //     return this.internalScreenPosition.x >= 0 && this.internalScreenPosition.x <= Game.canvas.width
    //         && this.internalScreenPosition.y >= 0 && this.internalScreenPosition.y <= Game.canvas.height;
    // }

    /**
     * @param {number} zoom 
     */
    setZoom(zoom) {
        this.zoom = zoom;
        if (this.zoom < Settings.projection.zoomOutScaleLimit) {
            this.zoom = Settings.projection.zoomOutScaleLimit;
        }
        if (this.zoom > Settings.projection.zoomInScaleLimit) {
            this.zoom = Settings.projection.zoomInScaleLimit;
        }
    }

    zoomOut() {
        this.zoom -= Settings.projection.zoomStep;
        if (this.zoom < Settings.projection.zoomOutScaleLimit) {
            this.zoom = Settings.projection.zoomOutScaleLimit;
        }
    }

    zoomIn() {
        this.zoom += Settings.projection.zoomStep;
        if (this.zoom > Settings.projection.zoomInScaleLimit) {
            this.zoom = Settings.projection.zoomInScaleLimit;
        }
    }

    /**
     * @param {GridBox} gridBox 
     * @param {Vector} nearbyOrigin 
     * @param {number} nearbyRadius 
     * @returns {boolean}
     */
    isNearby(gridBox, nearbyOrigin, nearbyRadius) {
        return this.isNearbyX(gridBox.origin.x, gridBox.getPixelWidth(), nearbyOrigin, nearbyRadius)
                && this.isNearbyY(gridBox.origin.y, gridBox.getPixelHeight(), nearbyOrigin, nearbyRadius);
    }

    /**
     * @param {number} x
     * @param {number} y
     * @param {Vector} nearbyOrigin 
     * @param {number} nearbyRadius 
     * @returns {boolean}
     */
    isNearbyXY(x, y, nearbyOrigin, nearbyRadius) {
        return this.isNearbyX(x, 1, nearbyOrigin, nearbyRadius) && this.isNearbyY(y, 1, nearbyOrigin, nearbyRadius);
    }

    /**
     * @param {Vector} position 
     * @param {number} size 
     * @returns {boolean}
     */
    isVisible(position, size) {
        return this.isVisibleX(position.x, size) && this.isVisibleY(position.y, size);
    }

    /**
     * @param {number} x 
     * @param {number} y 
     * @param {number} size 
     * @returns {boolean}
     */
    isVisibleXY(x, y, size) {
        return this.isVisibleX(x, size) && this.isVisibleY(y, size);
    }

    /**
     * @param {GridBox} gridBox 
     * @returns {boolean}
     */
    isVisibleGridBox(gridBox) {
        return this.isVisibleX(gridBox.origin.x, gridBox.getPixelWidth()) && this.isVisibleY(gridBox.origin.y, gridBox.getPixelHeight());
    }

    // isVisibleRectangle(rectangle) {
    //     return this.isVisibleX(rectangle.x, rectangle.width) && this.isVisibleY(rectangle.y, rectangle.height);
    // }

    // /**
    //  * @private
    //  * @param {number} x 
    //  * @param {number} width 
    //  * @returns {boolean}
    //  */
    // isVisibleX(x, width) {
    //     return (x < this.gridCenter.x + this.visibleHalfWidthWorld) && (x + width >= this.gridCenter.x - this.visibleHalfWidthWorld);
    // }

    /**
     * @private
     * @param {number} x 
     * @param {number} width 
     * @returns {boolean}
     */
    isVisibleX(x, width) {
        return (x < this.gridCenter.x + this.visibleHalfWidthWorld) && (x + width >= this.gridCenter.x - this.visibleHalfWidthWorld);
    }

    /**
     * @private
     * @param {number} y 
     * @param {number} height 
     * @returns {boolean}
     */
    isVisibleY(y, height) {
        return (y < this.gridCenter.y + this.visibleHalfHeightWorld) && (y + height >= this.gridCenter.y - this.visibleHalfHeightWorld);
    }

    /**
     * @returns {number}
     */
    getMaxVisibleHalfWidth() {
        return Math.max(this.visibleHalfHeightWorld, this.visibleHalfWidthWorld);
    }

    /**
     * @private
     * @param {number} x
     * @param {number} width 
     * @param {Vector} nearbyOrigin 
     * @param {number} nearbyRadius 
     * @returns {boolean} 
     */
    isNearbyX(x, width, nearbyOrigin, nearbyRadius) {
        return (x < nearbyOrigin.x + nearbyRadius) && (x + width >= nearbyOrigin.x - nearbyRadius);
    }

    /**
     * @private
     * @param {number} y
     * @param {number} height 
     * @param {Vector} nearbyOrigin 
     * @param {number} nearbyRadius 
     * @returns {boolean}
     */
    isNearbyY(y, height, nearbyOrigin, nearbyRadius) {
        return (y < nearbyOrigin.y + nearbyRadius) && (y + height >= nearbyOrigin.y - nearbyRadius);
    }

    // This is never called:

    /**
     * @param {Vector} screenCoordinate 
     * @param {number} screenWidth 
     * @param {number} screenHeight 
     */
    clampToScreen(screenCoordinate, screenWidth, screenHeight) {
        const x = Math.min(Math.max(screenCoordinate.x, 0), this.canvas.width - screenWidth);
        const y = Math.min(Math.max(screenCoordinate.y, 0), this.canvas.height - screenHeight);
        screenCoordinate.set(x, y);
    }
   
    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.resetProjectionState();
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetProjectionState();
    }

}